import { type ComponentType, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Typography,
  Divider,
} from "antd";
import { useForm } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { journalsQueries, journalQueryOptions, useJournalMutation } from "@entities/journal";
import { CloseIconLarge } from "@shared/assets/svg/icons";
import { useLocalUser } from "@shared/lib/hooks";
import { NOTIFICATION_MESSAGES } from "@shared/utils";
import "./reject-sign-modal.scss";

interface CancelSignModalProps extends Omit<ModalProps, "children" | "onOk"> {
  sectionTwoListId: string;
  itemNoVersionId: string;
  sectionTwoId: string;
  mode: "signer" | "inputter";
}

const queries = {
  inputter:
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/status/inputterReturn" as const,
  signer:
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/status/signerRejected" as const,
};

const modalContentMap = {
  inputter: {
    title: "Отозвать с подписания",
    description: "Вы действительно хотите отозвать с подписания?",
    confirmLabel: "Отозвать",
    errorMessage: "Не удалось отозвать запись",
  },
  signer: {
    title: "Отклонить подписание",
    description: "Вы действительно хотите отклонить подписание?",
    confirmLabel: "Отклонить",
    errorMessage: "Не удалось отклонить запись",
  },
};

type FormData = { comment?: string };

export function RejectSignModal({
  mode,
  sectionTwoListId,
  sectionTwoId,
  itemNoVersionId,
  onCancel,
  ...rest
}: CancelSignModalProps) {
  const queryClient = useQueryClient();

  const localUser = useLocalUser();

  // Определяем, обязателен ли комментарий
  const isCommentRequired = mode === "signer";
  const showComment = isCommentRequired;

  // Валидатор для комментария: обязателен только для signer
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onSubmit",
    defaultValues: { comment: "" },
    resolver: async (values) => {
      const errors: Record<string, any> = {};
      if (isCommentRequired && (!values.comment || !values.comment.trim())) {
        errors.comment = {
          type: "required",
          message: "Комментарий обязателен",
        };
      }
      return {
        values,
        errors,
      };
    },
  });

  // console.log(errors);

  const { mutateAsync, isPending } = useJournalMutation("post", queries[mode]);

  const handleSuccess = () => {
    // @ts-expect-error ошибка
    if (onCancel) onCancel();
    const listKey = journalsQueries.getSection2({ sectionTwoId }).queryKey;
    queryClient.invalidateQueries(
      { queryKey: [listKey[0], listKey[1]] },
      { cancelRefetch: false },
    );
    queryClient.invalidateQueries(
      journalQueryOptions(
        "get",
        "/api/v1/journals/section-two/items/{itemNoVersionId}",
        { params: { path: { itemNoVersionId } } },
      ),
    );
    notification.success({
      message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
      description: NOTIFICATION_MESSAGES.ENTRY_RECALLED_SUCCESS_ALT, // "Запись успешно отозвана"
    });
  };

  const handleOk = async (data: FormData) => {
    // data.comment может быть undefined для inputter, но обязателен для signer
    switch (mode) {
      case "inputter":
        mutateAsync({
          params: { path: { sectionTwoListId, itemNoVersionId } },
        })
          .then(() => {
            handleSuccess();
          })
          .catch(() =>
            notification.error({
              message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
              description: modalContentMap[mode].errorMessage,
            }),
          );
        break;
      case "signer":
        mutateAsync({
          params: { path: { sectionTwoListId, itemNoVersionId } },
          body: {
            comment: data.comment || "",
            signer: { signerEmployeeId: localUser?.user_id! },
          },
        })
          .then(() => {
            handleSuccess();
          })
          .catch(() =>
            notification.error({
              message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
              description: modalContentMap[mode].errorMessage,
            }),
          );
        break;
      default:
        break;
    }
  };

  const getLeftFooter = (
    OkBtn: ComponentType,
    CancelBtn: ComponentType,
  ): ReactNode => (
    <div className="journal-modal-footer-left">
      <OkBtn />
      <CancelBtn />
    </div>
  );

  return (
    <Modal
      centered
      className="reject-sign-modal"
      title={modalContentMap[mode].title}
      onCancel={onCancel}
      onOk={handleSubmit(handleOk)}
      closeIcon={<CloseIconLarge />}
      width={480}
      destroyOnClose
      okText={modalContentMap[mode].confirmLabel}
      okButtonProps={{
        disabled: showComment ? !isValid : false,
      }}
      footer={(_, { OkBtn, CancelBtn }) => getLeftFooter(OkBtn, CancelBtn)}
      confirmLoading={isPending}
      closable
      maskClosable
      {...rest}
    >
      <Typography className="journal-modal-description">
        {modalContentMap[mode].description}
      </Typography>

      {showComment && (
        <Form layout="vertical">
          <FormItem
            control={control}
            name="comment"
            style={{ color: "#333333", marginBottom: "16px" }}
            label="Причина"
            // @ts-expect-error rules Form.Item не совпадают с antd типами
            rules={[{ required: true, message: "Комментарий обязателен" }]}
          >
            <Input.TextArea
              autoSize
              style={{ minHeight: "56px" }}
              maxLength={255}
              showCount
            />
          </FormItem>
        </Form>
      )}
      {showComment && <Divider style={{ margin: "16px 0" }} />}
    </Modal>
  );
}
